import { FunctionComponent, useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import PrivateAdminPage from './PrivateAdminPage';
import NotFoundPage from '../NotFoundPage';
import PrivateSideBar from '../../components/PrivateSidebar';
import Footer from '../../components/Footer';
import { AuthState } from '../../reducers/types';
import { UserTypeValues } from '../../types';
import PrivateNavBar from '../../components/PrivateNavbar';
import PrivateStudiesPage from './PrivateStudiesPage';
import PrivateStatisticsPage from './PrivateStatisticsPage';
import PrivateStudyResultPage from './PrivateStudyResultPage';
import PrivateAdminStudiesPage from './PrivateAdminStudiesPage';
import PrivateMapPage from './PrivateMapPage';
import PrivateEstudioConexionPage from './PrivateEstudioConexionPage';
import PrivateEstudioConexionResultPage from './PrivateEstudioConexionResultPage';
import PrivateEstudioConexionMitigacionPage from './PrivateEstudioConexionMitigacionPage';
import PrivateEstudioConexionMitigacionResultPage from './PrivateEstudioConexionMitigacionResultPage';
import PrivateNodalHeatMapPage from './PrivateNodalHeatMapPage';

export type SidebarItem = {
  itemId: string;
  name: string;
  allowedUserTypes: Array<UserTypeValues | undefined>;
  childOrComponent?: Array<SidebarItem> | { path: string; render: JSX.Element };
  hidden?: boolean;
  icon?: string;
  disabled?: boolean;
};

const sideBarItems: Array<SidebarItem> = [
  {
    itemId: '01',
    name: 'Estudios',
    allowedUserTypes: [UserTypeValues.Admin, UserTypeValues.Interno],
    icon: 'clipboardListIcon',
    childOrComponent: {
      path: '/estudios',
      render: <PrivateStudiesPage />
    }
  },
  {
    itemId: '02',
    name: 'Diagnóstico de datos',
    allowedUserTypes: [UserTypeValues.Admin, UserTypeValues.Interno],
    disabled: true,
    icon: 'databaseSolidIcon',
    childOrComponent: { path: '/', render: <h1>Diagnóstico de datos</h1> }
  },
  {
    itemId: '03',
    name: 'Estadísticas',
    allowedUserTypes: [UserTypeValues.Admin, UserTypeValues.Interno],
    icon: 'chartBarSolidIcon',
    childOrComponent: { path: '/estadisticas', render: <PrivateStatisticsPage /> }
  },
  {
    itemId: '04',
    name: 'Mapa',
    allowedUserTypes: [UserTypeValues.Admin, UserTypeValues.Interno],
    disabled: false,
    icon: 'mapMarkedSolidIcon',
    childOrComponent: { path: '/mapa', render: <PrivateMapPage /> }
  },
  {
    itemId: '04b',
    name: 'Mapa de calor',
    allowedUserTypes: [UserTypeValues.Admin, UserTypeValues.Interno],
    disabled: false,
    icon: 'mapMarkedSolidIcon',
    childOrComponent: { path: '/mapa-calor', render: <PrivateNodalHeatMapPage /> }
  },
  {
    itemId: '05',
    name: 'Administrador',
    icon: 'gearSixIcon',
    allowedUserTypes: [UserTypeValues.Admin],
    childOrComponent: [
      {
        itemId: '0501',
        name: 'Usuarios',
        childOrComponent: { path: '/admin/usuarios', render: <PrivateAdminPage /> },
        allowedUserTypes: [UserTypeValues.Admin]
      },
      {
        itemId: '0502',
        name: 'Estudios',
        childOrComponent: { path: '/admin/estudios', render: <PrivateAdminStudiesPage /> },
        allowedUserTypes: [UserTypeValues.Admin]
      }
    ]
  },
  {
    itemId: '06',
    name: '',
    allowedUserTypes: [UserTypeValues.Admin, UserTypeValues.Interno],
    disabled: false,
    childOrComponent: {
      path: '/resultado-estudio/:simulacionId',
      render: <PrivateStudyResultPage />
    },
    hidden: true
  },
  {
    itemId: '07',
    name: '',
    allowedUserTypes: [UserTypeValues.Admin, UserTypeValues.Interno],
    childOrComponent: {
      path: '/estudio-conexion/nuevo',
      render: <PrivateEstudioConexionPage />
    },
    hidden: true
  },
  {
    itemId: '08',
    name: '',
    allowedUserTypes: [UserTypeValues.Admin, UserTypeValues.Interno],
    childOrComponent: {
      path: '/estudio-conexion/:id/resultado',
      render: <PrivateEstudioConexionResultPage />
    },
    hidden: true
  },
  {
    itemId: '09',
    name: '',
    allowedUserTypes: [UserTypeValues.Admin, UserTypeValues.Interno],
    childOrComponent: {
      path: '/estudio-conexion/:id/mitigaciones',
      render: <PrivateEstudioConexionMitigacionPage />
    },
    hidden: true
  },
  {
    itemId: '10',
    name: '',
    allowedUserTypes: [UserTypeValues.Admin, UserTypeValues.Interno],
    childOrComponent: {
      path: '/estudio-conexion/:id/resultado-mitigacion',
      render: <PrivateEstudioConexionMitigacionResultPage />
    },
    hidden: true
  }
];

const PrivatePage: FunctionComponent<{ auth: AuthState }> = ({ auth }) => {
  const allowedRoutes = useMemo(
    () =>
      extractPathAndRender(sideBarItems).filter((item) =>
        item.allowedUserTypes.includes(auth.user?.userTypeName)
      ),
    [auth]
  );

  const allowedSidebarItems = useMemo(
    () => (auth.user?.userTypeName ? filterHiddenItems(sideBarItems, auth.user?.userTypeName) : []),
    [auth]
  );

  /*
  the base path is /private
  E.g.: if the path '/estudios' we are is given it will redirect to '/private/estudios' we are
  */
  return (
    <>
      <PrivateNavBar auth={auth} />
      <div style={{ display: 'flex', flexDirection: 'row', flex: '1', position: 'relative' }}>
        <PrivateSideBar sideBarItems={allowedSidebarItems} />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: '1',
            overflow: 'auto',
            backgroundColor: 'var(--vdr-soft)'
          }}>
          <Routes>
            <Route path='/' element={<Navigate to='/private/estudios' />} />
            {allowedRoutes.map(({ path, render }, idx) => (
              <Route key={`private_route_${idx}`} path={path} element={render} />
            ))}
            <Route path='*' element={<NotFoundPage />} />
          </Routes>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default PrivatePage;

const extractPathAndRender = (
  items: Array<SidebarItem>
): Array<{
  path: string;
  render: JSX.Element;
  allowedUserTypes: Array<UserTypeValues | undefined>;
}> => {
  let result: Array<{
    path: string;
    render: JSX.Element;
    allowedUserTypes: Array<UserTypeValues | undefined>;
  }> = [];

  items.forEach((item) => {
    if (item.childOrComponent) {
      if (Array.isArray(item.childOrComponent)) {
        result = result.concat(extractPathAndRender(item.childOrComponent));
      } else if ('path' in item.childOrComponent && 'render' in item.childOrComponent) {
        result.push({
          path: item.childOrComponent.path,
          render: item.childOrComponent.render,
          allowedUserTypes: item.allowedUserTypes
        });
      }
    }
  });

  return result;
};

const filterHiddenItems = (
  items: Array<SidebarItem>,
  userType: UserTypeValues
): Array<SidebarItem> => {
  return items.reduce<Array<SidebarItem>>((filteredItems, item) => {
    if (item.hidden || !item.allowedUserTypes.includes(userType)) {
      return filteredItems;
    }

    let filteredChildOrComponent;
    if (Array.isArray(item.childOrComponent)) {
      filteredChildOrComponent = filterHiddenItems(item.childOrComponent, userType);
    } else {
      filteredChildOrComponent = item.childOrComponent;
    }

    if (Array.isArray(filteredChildOrComponent) && filteredChildOrComponent.length === 0) {
      filteredChildOrComponent = undefined;
    }

    filteredItems.push({
      ...item,
      childOrComponent: filteredChildOrComponent
    });

    return filteredItems;
  }, []);
};
